const MAX_UPLOAD_WIDTH = 1920;
const MAX_UPLOAD_HEIGHT = 1440;
const UPLOAD_JPEG_QUALITY = 0.85;


export async function resizeImageForUpload(
  source: File | Blob | string,
  preservePng = false,
): Promise<Blob> {
  const dataUrl = await toDataUrl(source);

  return new Promise<Blob>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;

      // Skip resize if already within limits
      if (width <= MAX_UPLOAD_WIDTH && height <= MAX_UPLOAD_HEIGHT) {
        if (typeof source === 'string') {
          dataUrlToBlob(dataUrl).then(resolve).catch(reject);
        } else {
          resolve(source);
        }
        return;
      }

      const scale = Math.min(MAX_UPLOAD_WIDTH / width, MAX_UPLOAD_HEIGHT / height);
      const targetW = Math.round(width * scale);
      const targetH = Math.round(height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0, targetW, targetH);

      // Preserve PNG for signatures (line art); use JPEG for photos
      const mimeType = preservePng ? 'image/png' : 'image/jpeg';
      const quality = preservePng ? undefined : UPLOAD_JPEG_QUALITY;
      canvas.toBlob(
        blob => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas toBlob failed'));
        },
        mimeType,
        quality,
      );
    };
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = dataUrl;
  });
}

async function toDataUrl(
  source: File | Blob | string,
): Promise<string> {
  if (typeof source === 'string') {
    return source;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(source);
  });
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
